import { companyConstants } from '../constants';
import {localizationHelpers} from '../../utils/helpers';

const defaultTeamEmployees = {
  searchQuery: {},
  pagination: {
    next_page: null,
    cursor: null,
  },
  users: []
}

const companyState = {
  id: null,
  costCenterTags: [],
  expenseTags: [],
  genericExpenseTags: [],
  invoiceTags: [],
  locationTags: [],
  projectTags: [],
  teamTags: [],
  vendorsTags: [],
  employees: [],
  logo: null,
  name: null,
  country: null,
  address: null,
  vat: null,
  currentCurrency: null,
  teamEmployees: defaultTeamEmployees,
  employee: {},
  features: {
    data: [],
    // `loaded` = the request succeeded; `settled` = it finished either way (see GET_COMPANY_FEATURES_FAILED).
    loaded: false,
    settled: false
  },
  settings: {},
  full_address: null,
  zip_code: null,
  zip_city: null
};


const companyReducer = (state = companyState, action) => {
  const {type, data, ...rest} = action;
  switch (type) {
    case companyConstants.GET_TAGS:
    case companyConstants.DELETE_TAG:
      return {
        ...state,
        costCenterTags: action.costCenterTags,
        expenseTags: action.expenseTags,
        genericExpenseTags: action.genericExpenseTags,
        invoiceTags: action.invoiceTags,
        locationTags: action.locationTags,
        projectTags: action.projectTags,
        teamTags: action.teamTags,
        vendorsTags: action.vendorsTags,
      };
    case companyConstants.UPDATE_TAGS:
      return {
        ...state,
        ...rest
      }
    case companyConstants.GET_EMPLOYEE:
      return {
        ...state,
        employee: data
      };
    case companyConstants.GET_EMPLOYEES:
      return {
        ...state,
        employees: data
      };
    case companyConstants.GET_TEAM_EMPLOYEES:
    case companyConstants.DELETE_EMPLOYEE:
    case companyConstants.RESEND_INVITE:
      return {
        ...state,
        teamEmployees: data
      };
    case companyConstants.CLEAR_TEAM_EMPLOYEES:
      return {
        ...state,
        teamEmployees: defaultTeamEmployees
      }
    case companyConstants.SET_COMPANY: {
      // `getCompanies` also dispatches SET_COMPANY to refresh the company already selected, and
      // nothing refetches tags/employees/etc. then (their effect is keyed on the id changing) — so
      // these are reset only when the id really changes.
      const isDifferentCompany = (data.company_id || null) !== state.id;
      return {
        ...state,
        ...(isDifferentCompany && {
          costCenterTags: companyState.costCenterTags,
          expenseTags: companyState.expenseTags,
          genericExpenseTags: companyState.genericExpenseTags,
          invoiceTags: companyState.invoiceTags,
          locationTags: companyState.locationTags,
          projectTags: companyState.projectTags,
          teamTags: companyState.teamTags,
          vendorsTags: companyState.vendorsTags,
          employees: companyState.employees,
          teamEmployees: companyState.teamEmployees,
          currentCurrency: companyState.currentCurrency,
          address: companyState.address,
          vat: companyState.vat,
          full_address: companyState.full_address,
          zip_code: companyState.zip_code,
          zip_city: companyState.zip_city
        }),
        country: localizationHelpers.getCountryCodeByName(data.country),
        name: data.name || null,
        id: data.company_id || null,
        logo: data.logo_url || null,
        settings: {},
        // Reset alongside settings: the new company's features haven't been fetched yet,
        // so any consumer gating on `loaded` (e.g. OverviewRouterPage's tier decision)
        // correctly waits instead of rendering off the previous company's stale data.
        features: {data: [], loaded: false, settled: false}
      };
    }
    case companyConstants.UPDATE_EMPLOYEE:
    case companyConstants.UPDATE_EMPLOYEE_STATUS:
      return {
        ...state,
        teamEmployees: action.teamEmployees,
        employee: data
      };
    case companyConstants.GET_SETTINGS:
      // MERGED, not replaced. Two endpoints once wrote this slot with different shapes — the full
      // record (19 keys) and a /settings/short that returned only {expenses_plan} — so whichever
      // landed last defined it, and a one-field response silently deleted `country`, blanking the
      // KYC company-type dropdown and Company Info (H1MVP-4794). The short call is gone, but a
      // replace here means any future partial response can do the same thing again.
      // Stale values across a company switch are not a risk: SET_COMPANY resets settings to {}.
      return {
        ...state,
        settings: {...state.settings, ...data}
      }
    case companyConstants.GET_COMPANY_INVOICE_EMAIL:
      return {
        ...state,
        settings: {...state.settings, ...data}
      }
    case companyConstants.CLEAR_COMPANY_DATA:
      return {
        ...companyState
      }
    case companyConstants.GET_COMPANY_INFORMATION:
      return {
        ...state,
        ...data
      }
    case companyConstants.GET_COMPANY_CURRENT_CURRENCY:
      return {
        ...state,
        currentCurrency: data
      }
    case companyConstants.GET_COMPANY_FEATURES:
      return {
        ...state,
        features: { data, loaded: true, settled: true }
      }
    // `loaded` stays false: other consumers (e.g. OverviewRouterPage's tier decision) read it as
    // "the data is real", and an empty list from a failed request is not an answer. `settled` lets
    // the route gate stop waiting, so every gated module reads unavailable and the path is a 404.
    case companyConstants.GET_COMPANY_FEATURES_FAILED:
      return {
        ...state,
        features: { data: [], loaded: false, settled: true }
      }
    case companyConstants.UPDATE_COMPANY_FEATURE: {
      const currentFeatures = state.features.data;
      const exists = currentFeatures.some(f => f.feature === action.featureName);
      const updatedFeatures = exists
        ? currentFeatures.map(f => f.feature === action.featureName ? { ...f, ...data } : f)
        : [...currentFeatures, data];
      return {
        ...state,
        features: { ...state.features, data: updatedFeatures }
      };
    }
    default:
      return state
  }
};

export default companyReducer;
